import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { useAuthStore } from '../stores/authStore'
import { validateContextSwitching, sanitizeOrganizadorContext } from '../utils/security'
import organizadoresService from '../services/organizadoresService'

export const OrganizadorContext = createContext()

export const useOrganizador = () => {
  const context = useContext(OrganizadorContext)
  if (!context) {
    throw new Error('useOrganizador deve ser usado dentro de OrganizadorProvider')
  }
  return context
}

export const OrganizadorProvider = ({ children }) => {
  const [organizadorAtivo, setOrganizadorAtivo] = useState(null)
  const [organizadores, setOrganizadores] = useState([])
  const [loading, setLoading] = useState(false)
  const { user } = useAuthStore()

  // Carregar dados completos do organizador
  const loadOrganizadorCompleto = async (organizadorId) => {
    try {
      setLoading(true)
      const organizador = await organizadoresService.getById(organizadorId)
      setOrganizadorAtivo(organizador)
      return organizador
    } catch (error) {
      console.error('Erro ao carregar organizador:', error)
      setOrganizadorAtivo({ id: organizadorId }) // Fallback
      return { id: organizadorId }
    } finally {
      setLoading(false)
    }
  }

  // Carregar organizador ativo do localStorage com validação
  useEffect(() => {
    if (!user) return
    
    const saved = localStorage.getItem('organizador_ativo')
    if (saved) {
      const parsed = JSON.parse(saved)
      const sanitized = sanitizeOrganizadorContext(user, parsed)
      if (sanitized) {
        // Sempre buscar dados frescos do servidor para garantir produtos_premium atualizados
        loadOrganizadorCompleto(sanitized.id)
      } else if (user.organizador_id) {
        // sanitize limpou o contexto (usuário não é super_admin) — carregar do próprio organizador
        loadOrganizadorCompleto(user.organizador_id)
      }
    } else if (user.organizador_id) {
      // Se não tem organizador salvo, buscar dados completos do usuário logado
      loadOrganizadorCompleto(user.organizador_id)
    }
  }, [user])

  // Salvar no localStorage quando mudar — ignora a primeira renderização (organizadorAtivo
  // ainda é null nesse ponto, antes do carregamento assíncrono acima terminar; sem essa guarda,
  // esse efeito apagava o contexto salvo antes da busca de loadOrganizadorCompleto resolver,
  // causando uma corrida onde o fallback para o organizador do próprio admin vencia às vezes)
  const persistInicializadaRef = useRef(false)
  useEffect(() => {
    if (!persistInicializadaRef.current) {
      persistInicializadaRef.current = true
      return
    }
    if (organizadorAtivo) {
      localStorage.setItem('organizador_ativo', JSON.stringify(organizadorAtivo))
    } else {
      localStorage.removeItem('organizador_ativo')
    }
  }, [organizadorAtivo])

  const switchToOrganizador = async (organizador) => {
    // BLINDAGEM: Só super_admin pode fazer context switching
    if (!validateContextSwitching(user)) {
      console.error('Acesso negado: Context switching não permitido')
      return
    }
    
    // Se só tem ID, buscar dados completos
    if (organizador && !organizador.fluxo_leilao) {
      await loadOrganizadorCompleto(organizador.id)
    } else {
      setOrganizadorAtivo(organizador)
    }
  }

  const clearOrganizador = () => {
    setOrganizadorAtivo(null)
  }

  const value = {
    organizadorAtivo,
    somenteLeitura: !!organizadorAtivo?.somente_visualizacao,
    organizadores,
    setOrganizadores,
    switchToOrganizador,
    clearOrganizador,
    loadOrganizadorCompleto,
    loading,
    isGlobalView: !organizadorAtivo
  }

  return (
    <OrganizadorContext.Provider value={value}>
      {children}
    </OrganizadorContext.Provider>
  )
}